import React, { Component } from 'react'
import { observer } from 'mobx-react'
import AnimateHeight from 'react-animate-height'
import i18n from 'i18next'

import Select from './Select'
import Input from './Input'
import Checkbox from './Checkbox'
import Date from './Date'
import CheckboxGroup from './CheckboxGroup'
import TextArea from './TextArea'
import ColorPicker from '../../UniformComponents/ColorPicker'

import './styles/form-controller.scss'
import TemplateAccess from '../TemplateAccess'
import RecordTag from '../RecordTags'
import TemplateCustomTags from '../TemplateCustomTags'

@observer
class FormController extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isCheckAccOpen: {},
        }
    }

    register = (name, validate, path) => {
        const { fields } = this.props
        if (path) {
            fields[name] = {
                validate,
                path,
            }
            return
        }
        fields[name] = validate
    }

    unregister = (name) => {
        const { fields } = this.props
        Reflect.deleteProperty(fields, name)
    }

    toggleCheckAcc = (index) => {
        this.setState((prevState) => ({
            isCheckAccOpen: {
                ...prevState.isCheckAccOpen,
                [index]: !prevState.isCheckAccOpen[index],
            },
        }))
    }

    renderCheckboxAccordion = (field, setField) => {
        const { isCheckAccOpen } = this.state
        return (
            <div className="check-acc">
                <div className="check-acc-header">
                    <i className="header-icon fa-solid fa-circle-info" />
                    <p className="header-label">{field.label}</p>
                </div>
                <div className="check-acc-body">
                    <hr className="acc-divider" />
                    <div className="option-list">
                        {Object.keys(field.fields).map((key, index) => (
                            <div className="option" key={`${key}_${index}`}>
                                <div
                                    className="option-heading"
                                    onClick={() => { this.toggleCheckAcc(index) }}
                                >
                                    <i className={`option-icon fa-regular ${isCheckAccOpen[index] ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                                    <p className="option-name">{key}</p>
                                    {this.renderForm([field.fields[key].selectAll], setField)}
                                </div>
                                <AnimateHeight
                                    className="option-acc"
                                    duration={250}
                                    height={isCheckAccOpen[index] ? 'auto' : 0}
                                >
                                    <hr className="option-divider" />
                                    <div className="checks">
                                        {this.renderForm(field.fields[key].options, setField)}
                                    </div>
                                </AnimateHeight>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    renderForm = (formFields, setField) => formFields.map((field) => {
        const { appStore } = this.props
        if (field.isHide) return null
        switch (field.type) {
            case 'select': {
                return (
                    <Select
                        register={this.register}
                        unregister={this.unregister}
                        defaultValue={field.defaultValue}
                        value={field.value}
                        onBlur={setField}
                        onChange={field.onChange}
                        options={field.options}
                        placeholder={field.placeholder}
                        label={field.label}
                        cssClass={field.cssClass || ''}
                        name={field.name}
                        isMandatory={field.isMandatory}
                        emptyError={field.errorMessage}
                        menuPlacement={field.menuPlacement}
                        path={field.path}
                        isMulti={field.isMulti}
                        isSearchable={field.isSearchable}
                        validate={field.validate}
                        showDisabled={field.showDisabled}
                        isClearable={field.isClearable}
                        tooltipText={field.tooltipText}
                    />
                )
            }
            case 'input': {
                return (
                    <Input
                        type={field.inputType || 'text'}
                        register={this.register}
                        unregister={this.unregister}
                        defaultValue={field.defaultValue}
                        value={field.value}
                        onBlur={setField}
                        onChange={field.onChange}
                        placeholder={field.placeholder}
                        label={field.label}
                        cssClass={field.cssClass || ''}
                        name={field.name}
                        isMandatory={field.isMandatory}
                        emptyError={field.errorMessage}
                        format={field.format}
                        formatError={field.formatError}
                        min={field.min}
                        max={field.max}
                        tooltipText={field.tooltipText}
                    />
                )
            }
            case 'textarea': {
                return (
                    <TextArea
                        key={field.name}
                        register={this.register}
                        unregister={this.unregister}
                        defaultValue={field.defaultValue}
                        onBlur={setField}
                        placeholder={field.placeholder}
                        label={field.label}
                        cssClass={field.cssClass}
                        textAreaCssStyle={field.textAreaCssStyle}
                        name={field.name}
                        isMandatory={field.isMandatory}
                        emptyError={`${field.label} ${i18n.t('required')}`}
                        rows={field.rows}
                        cols={field.cols}
                        tooltipText={field.tooltipText}
                        tooltipTitle={field.tooltipTitle}
                        tooltipLinkLabel={field.tooltipLinkLabel}
                        tooltipLink={field.tooltipLink}
                    />
                )
            }
            case 'checkbox': {
                return (
                    <Checkbox
                        register={this.register}
                        unregister={this.unregister}
                        onBlur={setField}
                        label={field.label}
                        labelDir={field.labelDir}
                        cssClass={field.cssClass || ''}
                        name={field.name}
                        defaultChecked={field.defaultChecked}
                        checked={field.checked}
                        disbaled={field.disbaled}
                        stopPropagation
                    />
                )
            }
            case 'date': {
                return (
                    <Date
                        register={this.register}
                        unregister={this.unregister}
                        name={field.name}
                        label={field.label}
                        placeholder={field.placeholder}
                        path={field.path}
                        isMandatory={field.isMandatory}
                        value={field.value}
                        minDate={field.minDate}
                        maxDate={field.maxDate}
                        onBlur={setField}
                        emptyError={field.errorMessage}
                        allowManualDateEdit={field.allowManualDateEdit}
                    />
                )
            }
            case 'checkbox-group': {
                return (
                    <CheckboxGroup
                        register={this.register}
                        unregister={this.unregister}
                        onBlur={setField}
                        label={field.label}
                        cssClass={field.cssClass || ''}
                        options={field.options}
                        name={field.name}
                        defaultChecked={field.value}
                        isMandatory={field.isMandatory}
                        returnValueUnchanged
                    />
                )
            }
            // checkbox-reactive is controlled by state change using checked
            case 'checkbox-reactive': {
                return (
                    <div className={`check-grp ${field.cssClass}`}>
                        <label className="check-grp-label">
                            {field.label}
                        </label>
                        <div className="check-grp-wrapper">
                            {this.renderForm(field.fields, setField)}
                        </div>
                    </div>
                )
            }
            case 'user-access-checkbox-accordion': {
                return this.renderCheckboxAccordion(field, setField)
            }
            case 'template-access-checkbox-accordion': return (appStore
                ? (
                    <TemplateAccess
                        store={appStore}
                        exposeUpdateBody={field.exposeUpdateBody}
                        initBody={field.initBody()}
                        isNewTemplate
                        isExploriTemplate={field.isExploriTemplate}
                    />
                ) : null)
            case 'template-custom-tags-accordion': return (appStore && field.isReady
                ? (
                    <TemplateCustomTags
                        appStore={appStore}
                        exposeUpdateBody={field.exposeUpdateBody}
                        templateParams={field.templateParams}
                        isNewTemplate
                    />
                ) : null)
            case 'custom-tag-fields': return (
                <RecordTag
                    values={field.values}
                    availableTags={field.options}
                    onBlur={field.onBlur}
                />
            )
            case 'group': {
                const { elements, className } = field
                return (
                    <div className={className}>
                        {this.renderForm(elements, setField)}
                    </div>
                )
            }
            case 'color-picker': return (
                <ColorPicker
                    name={field.name}
                    label={field.label || field.name}
                    defaultValue={field.defaultValue}
                    position={field.position}
                    onBlur={setField}
                    showPreview={field.showPreview}
                />
            )
            case 'custom': return field.comp
            default: return null
        }
    })

    render() {
        const { formFields, setField } = this.props
        return (
            <div className="field-wrapper">
                {this.renderForm(formFields, setField)}
            </div>
        )
    }
}

export default FormController
